/** Metadatos de salida de episodios (espejo de server/episodeAnalysis.js OUTPUT_META). */

import { fieldAlias, headerLabel } from '../components/chartBuilder/LiveDatasetTable';
import { DURATION_FIELD_BASES, buildTableDurationFormats, buildTableDurationTooltipFormats, buildYDurationChartConfig } from './durationFormat';

const FINE_BUCKETS = new Set(['raw', 'minute', 'hour']);

export const EPISODE_OUTPUT_META = {
  episodes: {
    fields: ['inicio', 'fin', 'duracion_min', 'hora_inicio', 'dia'],
    numeric: ['duracion_min'],
    duration: ['duracion_min'],
    defaultSort: { field: 'inicio', dir: 'asc' },
  },
  totalPerDay: {
    fields: ['dia', 'total_min', 'episodios'],
    numeric: ['total_min', 'episodios'],
    duration: ['total_min'],
    defaultSort: { field: 'dia', dir: 'asc' },
  },
  maxPerDay: {
    fields: ['dia', 'max_min', 'inicio'],
    numeric: ['max_min'],
    duration: ['max_min'],
    defaultSort: { field: 'dia', dir: 'asc' },
  },
  totalPerGroup: {
    fields: ['total_min', 'episodios', 'max_min'],
    numeric: ['total_min', 'episodios', 'max_min'],
    duration: ['total_min', 'max_min'],
    defaultSort: { field: 'total_min', dir: 'desc' },
  },
  maxPerGroup: {
    fields: ['max_min', 'inicio', 'fin'],
    numeric: ['max_min'],
    duration: ['max_min'],
    defaultSort: { field: 'max_min', dir: 'desc' },
  },
};

export const EPISODE_OUTPUT_LABELS = {
  episodes: 'Episodios (inicio, fin, duración)',
  totalPerDay: 'Total de minutos por día',
  maxPerDay: 'Episodio más largo por día',
  totalPerGroup: 'Total por grupo (ranking)',
  maxPerGroup: 'Episodio más largo por grupo',
};

/** Opciones de formato de la tabla de episodios (no confundir con el pipeline del gráfico). */
export const EPISODE_OUTPUT_OPTIONS = [
  {
    value: 'episodes',
    label: EPISODE_OUTPUT_LABELS.episodes,
    hint: 'Una fila por episodio detectado. Las columnas de grupo se conservan.',
  },
  {
    value: 'totalPerDay',
    label: EPISODE_OUTPUT_LABELS.totalPerDay,
    hint: 'Suma de duraciones por día.',
  },
  {
    value: 'maxPerDay',
    label: EPISODE_OUTPUT_LABELS.maxPerDay,
    hint: 'Duración máxima de cada día y su hora de inicio.',
  },
  {
    value: 'totalPerGroup',
    label: EPISODE_OUTPUT_LABELS.totalPerGroup,
    hint: 'Una fila por grupo con minutos totales y cantidad de episodios.',
  },
  {
    value: 'maxPerGroup',
    label: EPISODE_OUTPUT_LABELS.maxPerGroup,
    hint: 'El episodio más largo de cada grupo con inicio y fin.',
  },
];

export const EPISODE_FILTER_OPERATORS = [
  { value: 'eq', label: 'Igual a' },
  { value: 'neq', label: 'Distinto de' },
  { value: 'gt', label: 'Mayor que' },
  { value: 'gte', label: 'Mayor o igual' },
  { value: 'lt', label: 'Menor que' },
  { value: 'lte', label: 'Menor o igual' },
  { value: 'contains', label: 'Contiene' },
  { value: 'ncontains', label: 'No contiene' },
];

function isValueCol(c) {
  return (
    c?.role === 'value' ||
    c?.fieldRole === 'value' ||
    c?.jsonMode === 'arrayCount' ||
    c?.jsonMode === 'arraySum' ||
    (c?.aggregation && c.aggregation !== 'none')
  );
}

function isLikelyTimeColumn(c) {
  const col = String(c?.column || '').toLowerCase();
  const alias = String(c?.alias ?? '').trim().toLowerCase();
  if (
    col === 'time' ||
    col === 'timestamp' ||
    col === 'timestamptz' ||
    col.includes('fecha') ||
    col.endsWith('_at') ||
    col.endsWith('_time')
  ) {
    return true;
  }
  if (
    alias === 'time' ||
    alias === 'timestamp' ||
    alias.includes('fecha') ||
    alias.endsWith('_at') ||
    alias.endsWith('_time')
  ) {
    return true;
  }
  const bucket = c?.dateBucket;
  if (
    bucket &&
    bucket !== 'dayOfWeek' &&
    bucket !== 'hourOfDay' &&
    bucket !== 'weekOfYear'
  ) {
    return true;
  }
  return false;
}

function isUnnestCol(c) {
  return c?.jsonMode === 'arrayUnnest' || c?.jsonMode === 'array_unnest';
}

/** Índice de la columna de tiempo fina (misma lógica que el servidor). */
export function pickEpisodeTimeIndex(columns) {
  const candidates = (columns || [])
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => c?.tableId && c?.column && !isValueCol(c) && !isUnnestCol(c));

  const timeLike = candidates.filter(({ c }) => isLikelyTimeColumn(c));
  const pool = timeLike.length ? timeLike : candidates;

  const explicit = pool.find(
    ({ c }) => c.dateBucket === 'minute' || c.dateBucket === 'hour'
  );
  if (explicit) return explicit.i;
  const raw = pool.find(({ c }) => !c.dateBucket || c.dateBucket === 'raw');
  if (raw) return raw.i;
  const day = pool.find(({ c }) => c.dateBucket === 'day');
  if (day) return day.i;
  return pool[0] ? pool[0].i : -1;
}

const DEFAULT_UNNEST_FIELDS = [
  { path: 'frame.x', alias: 'x' },
  { path: 'frame.y', alias: 'y' },
];

/** Alias SQL de un campo unnest (misma regla que server/jsonArrayExpr.js). */
export function unnestFieldAlias(field, index) {
  if (field?.alias && String(field.alias).trim()) {
    return String(field.alias).trim().replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 60);
  }
  const base = String(field?.path || `field_${index}`)
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .slice(0, 60);
  return base || `unnest_${index}`;
}

function unnestFieldsForColumn(col) {
  if (!isUnnestCol(col)) return [];
  return col.unnestFields?.length ? col.unnestFields : DEFAULT_UNNEST_FIELDS;
}

function isUnnestFieldOption(opt) {
  return Boolean(opt?.isUnnestField);
}

const TIME_LIKE_FIELD_BASES = new Set([
  'periodo',
  'time',
  'timestamp',
  'inicio',
  'fin',
  'dia',
  'hora_inicio',
]);

function isTimeLikeOutputField(f) {
  const base = String(f?.base || f?.value || '').toLowerCase();
  if (TIME_LIKE_FIELD_BASES.has(base)) return true;
  return base.includes('periodo') || base.includes('time') || base.includes('fecha');
}

/** Opciones tiempo/medida cuando episodios se alimentan de una tabla derivada (p. ej. zonas). */
export function buildEpisodeColumnOptionsFromOutputFields(fields) {
  const timeOptions = [];
  const valueOptions = [];
  (fields || []).forEach((f) => {
    const opt = {
      value: f.value,
      label: String(f.value).replace(/_/g, ' '),
      synthetic: true,
    };
    if (isTimeLikeOutputField(f)) {
      timeOptions.push(opt);
    } else if (f.numeric) {
      valueOptions.push(opt);
    }
  });
  if (!timeOptions.length) {
    (fields || []).filter((f) => !f.numeric).forEach((f) => {
      if (!valueOptions.some((o) => o.value === f.value)) {
        timeOptions.push({
          value: f.value,
          label: String(f.value).replace(/_/g, ' '),
          synthetic: true,
        });
      }
    });
  }
  return { timeOptions, valueOptions };
}

export function resolveEpisodeSeriesColumnsFromOutputFields(fields, cfg) {
  const config = cfg || {};
  const { timeOptions, valueOptions } = buildEpisodeColumnOptionsFromOutputFields(fields);
  let timeOpt = config.timeColumn
    ? timeOptions.find((o) => o.value === config.timeColumn) ||
      valueOptions.find((o) => o.value === config.timeColumn) ||
      null
    : null;
  let valueOpt = config.valueColumn
    ? valueOptions.find((o) => o.value === config.valueColumn) ||
      timeOptions.find((o) => o.value === config.valueColumn) ||
      null
    : null;
  if (!timeOpt) timeOpt = timeOptions[0] || null;
  if (!valueOpt) valueOpt = valueOptions.find((o) => o.value !== timeOpt?.value) || valueOptions[0] || null;
  return {
    ready: Boolean(timeOpt && valueOpt),
    timeColumn: timeOpt?.value || '',
    valueColumn: valueOpt?.value || '',
    timeCol: null,
    valueCol: null,
    timeOpt,
    valueOpt,
    timeOptions,
    valueOptions,
    fromOutputFields: true,
  };
}

export function resolveEpisodeGroupColsFromOutputFields(fields, cfg) {
  const config = cfg || {};
  const wantTime = String(config.timeColumn || '').trim().toLowerCase();
  const wantValue = String(config.valueColumn || '').trim().toLowerCase();
  return (fields || [])
    .filter((f) => {
      const v = String(f.value || '').toLowerCase();
      if (wantTime && v === wantTime) return false;
      if (wantValue && v === wantValue) return false;
      return true;
    })
    .map((f) => ({ base: f.base || f.value, value: f.value, synthetic: true }));
}

/** Alias SQL de una columna del dataset (misma regla que el servidor: col_${i}). */
export function episodeColumnAlias(c, index) {
  return fieldAlias(c, `col_${index}`);
}

/** Opciones de columnas fecha/eje y medida para el análisis de episodios. */
export function buildEpisodeColumnOptions(columns, context) {
  const timeOptions = [];
  const valueOptions = [];
  (columns || []).forEach((c, i) => {
    if (!c?.tableId || !c?.column) return;
    if (isUnnestCol(c)) {
      const parentLabel = headerLabel(c, context) || episodeColumnAlias(c, i);
      unnestFieldsForColumn(c).forEach((field, fi) => {
        const alias = unnestFieldAlias(field, fi);
        const fieldLabel = field.alias || field.path || alias;
        valueOptions.push({
          value: alias,
          label: `${parentLabel} · ${fieldLabel}`,
          index: i,
          isUnnestField: true,
        });
      });
      return;
    }
    const value = episodeColumnAlias(c, i);
    const label = headerLabel(c, context) || value;
    if (isValueCol(c)) {
      valueOptions.push({ value, label, index: i });
    } else {
      timeOptions.push({ value, label, index: i });
    }
  });
  return { timeOptions, valueOptions };
}

function pickAutoTimeOption(timeOptions, columns) {
  const withCol = timeOptions.map((o) => ({ ...o, column: columns[o.index] }));
  const timeLike = withCol.filter(({ column: c }) => isLikelyTimeColumn(c));
  const pool = timeLike.length ? timeLike : withCol;
  const minuteHour = pool.find(({ column: c }) => c?.dateBucket === 'minute' || c?.dateBucket === 'hour');
  if (minuteHour) return minuteHour;
  const raw = pool.find(({ column: c }) => !c?.dateBucket || c.dateBucket === 'raw');
  if (raw) return raw;
  return pool[0] || null;
}

/** Si la columna guardada no parece fecha/hora pero hay otra que sí, ignorar la guardada. */
function resolveConfiguredTimeOption(config, timeOptions, columns) {
  const want = String(config?.timeColumn || '').trim();
  if (!want) return null;
  const hit = timeOptions.find((o) => o.value === want) || null;
  if (!hit) return null;
  const col = columns[hit.index];
  if (isLikelyTimeColumn(col)) return hit;
  const auto = pickAutoTimeOption(timeOptions, columns);
  if (auto && auto.value !== hit.value && isLikelyTimeColumn(columns[auto.index])) {
    return auto;
  }
  return hit;
}

/** Resuelve columnas de tiempo y medida (config explícita o auto-detección). */
export function resolveEpisodeSeriesColumns(columns, cfg, context = null) {
  const config = cfg || {};
  const { timeOptions, valueOptions } = buildEpisodeColumnOptions(columns, context);
  let timeOpt = resolveConfiguredTimeOption(config, timeOptions, columns);
  let valueOpt = config.valueColumn
    ? valueOptions.find((o) => o.value === config.valueColumn) || null
    : null;
  if (!timeOpt) timeOpt = pickAutoTimeOption(timeOptions, columns);
  if (!valueOpt) valueOpt = valueOptions[0] || null;
  return {
    ready: Boolean(timeOpt && valueOpt),
    timeColumn: timeOpt?.value || '',
    valueColumn: valueOpt?.value || '',
    timeCol: timeOpt && !isUnnestFieldOption(timeOpt) ? columns[timeOpt.index] : null,
    valueCol: valueOpt && !isUnnestFieldOption(valueOpt) ? columns[valueOpt.index] : null,
    timeOpt,
    valueOpt,
    timeOptions,
    valueOptions,
  };
}

export function episodeConfigReady(columns, cfg, context = null) {
  return resolveEpisodeSeriesColumns(columns, cfg, context).ready;
}

/** Columnas elegibles como groupColumn: cualquier columna salvo tiempo/medida elegidos y unnest. */
export function resolveEpisodeGroupCols(columns, cfg) {
  const cols = columns || [];
  const config = cfg || {};
  const wantTime = String(config.timeColumn || '').trim().toLowerCase();
  const wantValue = String(config.valueColumn || '').trim().toLowerCase();

  const groups = [];
  cols.forEach((c, i) => {
    if (!c?.tableId || !c?.column) return;
    if (isUnnestCol(c)) return;
    const alias = episodeColumnAlias(c, i);
    const base = String(c?.alias ?? '').trim() || String(c?.column || '');
    if (wantTime && (alias.toLowerCase() === wantTime || base.toLowerCase() === wantTime)) return;
    if (wantValue && (alias.toLowerCase() === wantValue || base.toLowerCase() === wantValue)) return;
    groups.push({ index: i, base });
  });
  return groups;
}

export function applyEpisodePreserveGroupColumns(groups, cfg) {
  const keep = cfg?.preserveGroupColumns;
  if (!Array.isArray(keep) || keep.length === 0) return groups || [];
  const want = new Set(keep.map((k) => String(k).toLowerCase()));
  return (groups || []).filter((g) => want.has(String(g.base).toLowerCase()));
}

/** Opciones de columna de grupo para partición / calibración (excluye tiempo y medida). */
export function buildEpisodeGroupColumnOptions(eligibleGroupCols) {
  return (eligibleGroupCols || []).map((g) => ({
    value: g.base,
    label: String(g.base).replace(/_/g, ' '),
  }));
}

export function resolveEligibleEpisodeGroupCols(columns, cfg, { episodeInputFields = null } = {}) {
  if (episodeInputFields) {
    return resolveEpisodeGroupColsFromOutputFields(episodeInputFields, cfg);
  }
  return resolveEpisodeGroupCols(columns, cfg);
}

export function resolveEffectiveEpisodeGroupCols(columns, cfg, options = {}) {
  return applyEpisodePreserveGroupColumns(resolveEligibleEpisodeGroupCols(columns, cfg, options), cfg);
}

/** ¿La columna es la fecha fina de la serie? */
export function isEpisodeTimeColumn(column, columns) {
  if (!column) return false;
  const idx = columns.indexOf(column);
  return idx >= 0 && idx === pickEpisodeTimeIndex(columns);
}

/**
 * Campos finales del resultado (grupos primero, luego campos de salida),
 * con renombres y desambiguación igual que el servidor.
 */
export function episodeOutputFields(cfg, groupCols = []) {
  const output = cfg?.output || 'episodes';
  const meta = EPISODE_OUTPUT_META[output] || EPISODE_OUTPUT_META.episodes;
  const labels = cfg?.columnLabels || {};
  const used = new Set();
  const uniquify = (wanted) => {
    let candidate = wanted;
    let n = 2;
    while (used.has(candidate)) {
      candidate = `${wanted}_${n}`;
      n += 1;
    }
    used.add(candidate);
    return candidate;
  };

  const fields = [];
  for (const g of groupCols) {
    fields.push({
      base: g.base,
      value: uniquify(String(labels[g.base] ?? '').trim() || g.base),
      numeric: false,
      isGroup: true,
    });
  }
  for (const base of meta.fields) {
    fields.push({
      base,
      value: uniquify(String(labels[base] ?? '').trim() || base),
      numeric: meta.numeric.includes(base),
      duration: (meta.duration || DURATION_FIELD_BASES.filter((b) => meta.numeric?.includes(b))).includes(base),
      isGroup: false,
    });
  }
  return fields;
}

/** fieldKeys finales (solo nombres visibles). */
export function episodeOutputFieldKeys(cfg, groupCols = []) {
  return episodeOutputFields(cfg, groupCols).map((f) => f.value);
}

/** Resuelve seriesField del builder al nombre final de columna en el resultado de episodios. */
export function resolveEpisodeSeriesLabel(seriesField, cfg, groupCols = []) {
  if (!seriesField || String(seriesField).startsWith('__trset__:')) return undefined;
  const fields = episodeOutputFields(cfg, groupCols);
  const hit = fields.find((f) => f.value === seriesField || f.base === seriesField);
  return hit?.value;
}

/** Etiqueta legible para una salida. */
export function episodeOutputDescription(output) {
  switch (output) {
    case 'totalPerDay':
      return 'el total de minutos por día';
    case 'maxPerDay':
      return 'el episodio más largo por día';
    case 'totalPerGroup':
      return 'el total de minutos por grupo (ranking)';
    case 'maxPerGroup':
      return 'el episodio más largo por grupo';
    default:
      return 'los episodios detectados';
  }
}

/** Formatos de duración para tablas y gráficos publicados con episodios. */
export function resolveEpisodeDurationPresentation(episodeAnalysis, tableColumns, yColumn) {
  if (!episodeAnalysis?.enabled || episodeAnalysis.chartSource === 'original') {
    return {
      tableDurationFormats: undefined,
      tableDurationTooltipFormats: undefined,
      yDuration: null,
    };
  }
  const groupCols = resolveEpisodeGroupCols(tableColumns || [], episodeAnalysis || {});
  const outputFields = episodeOutputFields(episodeAnalysis, groupCols);
  return {
    tableDurationFormats: buildTableDurationFormats(episodeAnalysis, outputFields),
    tableDurationTooltipFormats: buildTableDurationTooltipFormats(episodeAnalysis, outputFields),
    yDuration: buildYDurationChartConfig(episodeAnalysis, outputFields, yColumn),
  };
}

export { FINE_BUCKETS, isValueCol };
